import React, { useState } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import compose from 'helpers/compose';

import Container from '@material-ui/core/Container';
import Tab from '@material-ui/core/Tab';

import Tabs from 'components/CustomTabs/CustomTabs';

import RegularFees from './RegularFees';
import SingleFees from './SingleFees';
import { AmountValue } from 'common/interfaces';
import { DetailPage } from 'pages/Accounts/Detail/enums';
import { RootStore } from 'stores/rootStore';
import { AccountMode } from 'enums';
//import { Currency } from 'enums';


export interface FeeLine {
  title?: string;
  total: number;
  ocp: number;
  iz: number;
  vat?: number;
}

export interface FeesOptions {
  FROM_0_TO_1M: number[];
  FROM_1_TO_5M: number[];
  FROM_5_TO_10M: number[];
  FROM_10_TO_20M: number[];
  OVER_20M: number[];
  LOW: number[];
  maxPerformance: number[];
}


export interface FeesLimits {
  PERFORMANCE?: { min: number; max: number };
  FROM_0_TO_1M?: { min: number; max: number };
  FROM_1_TO_5M?: { min: number; max: number };
  FROM_5_TO_10M?: { min: number; max: number };
  FROM_10_TO_20M?: { min: number; max: number };
  OVER_20M?: { min: number; max: number };
  LOW?: { min: number; max: number };
}

export interface SingleFeesData {
  prepaidApplied: boolean;
  prepaidIndividual: boolean;
  continuousIndividual: boolean;
  cappingApplied: boolean;

  feeRate: number;

  defaultPlanningYears: number;
  calculatedFee: AmountValue;
  cappedFee?: AmountValue;
  discount: number;
  individualFee: AmountValue;
  individualRate: number;
  prepaidCalculatedFee: AmountValue;
  rateDiscount: number;
  periodicDeposit: AmountValue;
  periodicDeposits: AmountValue;
  regularCalculatedFee: AmountValue;
  totalRegularCalculatedFee: AmountValue;
  initialDeposit: AmountValue;
  singleCalculatedFee: AmountValue;
  totalInvestments: AmountValue;
  employer?: AmountValue;
  employerInvestments?: AmountValue;
  clientInvestments?: AmountValue;
  type: 'PREPAID' | 'CONTINUOUS';
  length: { years: number; months: number };

  periodicMonths: { years: number; months: number };

  prepaidRemaining?: AmountValue;
  deposits?: AmountValue;

  closingFee: AmountValue; //nf: check type
  transferFromFee: AmountValue; //nf: check type
  transferToFee: number;

  options: {
    types: string[];
    years: number[];
  };

  draftExists?: boolean;
}

export interface RegularFeesData {
  individual: boolean;
  customValues: boolean;
  options: FeesOptions;
  common: {
    accountManagementFee: AmountValue;
    custodyFee: AmountValue;
    reallocationFee: AmountValue;
    rebalancingFee: AmountValue;
    reportingFee: AmountValue;
    hedgingFee: AmountValue;
    averageTer: number;
    spotHedgingFee: number;
    forexFee: number;
  };
  performance: FeeLine;
  service: FeeLine[];
  serviceLow: FeeLine;
  validFrom?: string;

  draftExists?: boolean;
  templateExists?: boolean;

  izFeeSavingsRuleApplied?: boolean;
}

interface Props {
  readOnly?: boolean;
  detailMode?: boolean;
  accountMode?: AccountMode;
  accountCurrency?: string;
  rootStore?: RootStore;
}

const Fees: React.FC<Props> = ({ readOnly, detailMode, accountMode, accountCurrency, rootStore }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(DetailPage.SINGLE_FEES);
  accountMode = accountMode || rootStore?.stores.editAccountStore.mode || AccountMode.STANDARD;

  // value?: number should be value: number, but the @material-ui typing is bugged
  const handleTabChange = (_event: React.ChangeEvent<{}>, value?: DetailPage) => {
    if (value !== undefined) {
      setTab(value);
    }
  };

  return (
    <Container maxWidth="md">
      <Tabs value={tab} onChange={handleTabChange}>
        <Tab value={DetailPage.SINGLE_FEES} label={t('fees_single_title')} id="fees-single-tab" />
        <Tab
          label={t('fees_regular_title')}
          disabled={tab === DetailPage.SINGLE_FEES && !readOnly}
          id="fees-regular-tab"
          value={DetailPage.REGULAR_FEES}
        />
      </Tabs>

      {tab === DetailPage.SINGLE_FEES && (
        <SingleFees detailMode={detailMode} accountCurrency={accountCurrency} readOnly={readOnly} tabChange={setTab} />
      )}
      {tab === DetailPage.REGULAR_FEES && (
        <RegularFees
          detailMode={detailMode}
          accountMode={accountMode}
          accountCurrency={accountCurrency}
          readOnly={readOnly}
          tabChange={setTab}
        />
      )}
    </Container>
  );
};

export default compose(inject('rootStore'), observer)(Fees);
//export default Fees;
